/**
 * THE SIGN-IN PAGE'S SHEET (PV5-8:B; surfaces.md, sign in): the parts only this page draws. One <style id="pv-auth-style">,
 * put in by the page through the shell's sheet hook (usePageSheet) and taken out when the page leaves.
 *
 * SCOPE AND WEIGHT. Every rule starts on the page's own root (`.pv.pv-auth`: the frame sets `pv-auth` on the `.pv` root
 * itself), so an override of the stage's or the kit's rules wins by weight (0,3,0 over their 0,2,0), never by where the
 * sheets land. Every value is a role or a material from the token SSOT (--pv-*); the frost, the satin, the wells and the
 * danger ink are the shell's and the kit's, reused.
 *
 * The reasons live here, not in the sheet's text: a comment in the text ships to every visitor.
 */

export const AUTH_STYLE_ID = 'pv-auth-style';

const P = '.pv.pv-auth';

/*
 * THE STAGE, FOR A FORM. The card holds a form, so it stands a little wider than the stage's (420px against 400) and
 * keeps 360px on a tablet. On a phone the card fills the width and the floor is a 96px band (definitions.md, sign in),
 * so the form sits in the first screen. The welcome line lies on the ground at the left in ink-2, lowercased, with "AI"
 * keeping the capitals it is written with (`text-transform: none`: nothing on the platform is forced into capitals,
 * tests/policy/noUppercase.test.ts); in the invitation-code journey the steps stand there instead.
 */
const STAGE = `
${P} .pv-standing { width: min(420px, 38vw); }
@media (max-width: 1080px) { ${P} .pv-standing { width: min(380px, 44vw); } }
@media (max-width: 719px) {
  ${P} .pv-standing { width: 100%; max-width: 460px; }
  ${P} .pv-stage-pool { height: 96px; }
  ${P} .pv-stage-copy { padding-bottom: 24px; }
}
${P} .pv-auth-welcome { margin: 0; max-width: 560px; font-weight: 700; font-size: clamp(30px, 3.4vw, 46px); line-height: 1.12; letter-spacing: -.03em;
  color: var(--pv-ink-2); text-transform: lowercase; text-wrap: balance; }
${P} .pv-auth-ai { text-transform: none; }
@media (max-width: 719px) { ${P} .pv-auth-welcome { font-size: 28px; } }
`;

/*
 * THE JOURNEY on the ground (the invitation-code visit): the steps as a list, the current one in ink with the gold disc,
 * the ones done with a check, the rest in ink-3; the current step's time under it; the note that the code is saved.
 */
const JOURNEY = `
${P} .pv-auth-steps { list-style: none; margin: 14px 0 0; padding: 0; display: grid; gap: 10px; }
${P} .pv-auth-steps li { display: flex; align-items: center; gap: 12px; font: 600 16px/1.3 var(--pv-font); color: var(--pv-ink-3); }
${P} .pv-auth-steps li[data-state="active"] { color: var(--pv-ink); }
${P} .pv-auth-steps li[data-state="done"] { color: var(--pv-ink-2); }
${P} .pv-auth-step-dot { flex: none; display: inline-grid; place-items: center; width: 28px; height: 28px; border-radius: 50%; font: 700 13px/1 var(--pv-font);
  border: 1px solid var(--pv-line-control); background: var(--pv-well); color: var(--pv-ink-2); }
${P} .pv-auth-steps li[data-state="active"] .pv-auth-step-dot { border-color: transparent; color: var(--pv-accent-ink);
  background: linear-gradient(180deg, var(--pv-gold-sky), var(--pv-accent) 55%, var(--pv-gold-mid)); }
${P} .pv-auth-step-hint { margin: 16px 0 0; font: 500 15px/1.5 var(--pv-font); color: var(--pv-ink-2); }
${P} .pv-auth-step-time { color: var(--pv-accent-text); font-weight: 600; }
`;

/*
 * THE CARD. The title, the two wet-stone fields, the row under them (remember me, forgot password), the gold action at
 * full width, the working line and the failure under the actions, "or", and Google's own button in a liquid-glass frame
 * (the ring goes on when the page points at it for a Google-only account). A caller's hint stands above the card's
 * title. The fields keep the kit's size; the card's own gaps are the page's. "forgot password?" is a 44px target whose
 * underline is the text's own (the header action's gold underline), never the link's bottom border, which a 44px box
 * would draw well under the words.
 */
const CARD = `
${P} .pv-signin { display: flex; flex-direction: column; gap: 16px; }
${P} .pv-signin-title { margin: 0 0 4px; font-weight: 700; font-size: 24px; line-height: 1.2; letter-spacing: -.02em; color: var(--pv-ink); text-transform: lowercase; }
${P} .pv-signin-form { display: flex; flex-direction: column; gap: 14px; }
${P} .pv-signin .pv-k-field { max-width: none; }
${P} .pv-signin-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
${P} .pv-signin-forgot { min-height: 44px; padding: 0; border: 0; background: none; cursor: pointer; font-size: 14.5px; text-transform: lowercase;
  text-decoration: underline; text-decoration-thickness: 1px; text-underline-offset: 4px; text-decoration-color: color-mix(in srgb, var(--pv-accent-text) 45%, transparent); }
${P} .pv-signin-forgot:hover:not(:disabled) { text-decoration-color: var(--pv-accent-text); }
${P} .pv-signin-forgot:focus-visible { outline: 2px solid var(--pv-focus-ring); outline-offset: 2px; border-radius: 4px; }
${P} .pv-signin-forgot:disabled { opacity: .45; cursor: default; }
${P} .pv-signin .pv-k-btn { width: 100%; }
${P} .pv-signin-btn-suffix { font-weight: 500; opacity: .85; }
${P} .pv-signin-line { margin: 0; font: 500 14.5px/1.5 var(--pv-font); color: var(--pv-ink-2); text-transform: lowercase; }
${P} .pv-signin-line strong { color: var(--pv-ink); font-weight: 600; text-transform: none; }
${P} .pv-signin-line-sub { color: var(--pv-ink-3); }
${P} .pv-signin-failure { display: flex; flex-direction: column; align-items: flex-start; gap: 10px; }
${P} .pv-signin-failure .pv-k-btn { width: auto; }
${P} .pv-signin-note-title { display: block; margin-bottom: 2px; }
${P} .pv-signin-or { display: flex; align-items: center; gap: 12px; margin: 2px 0; font: 500 13.5px/1 var(--pv-font); color: var(--pv-ink-3); text-transform: lowercase; }
${P} .pv-signin-or::before, ${P} .pv-signin-or::after { content: ""; flex: 1; height: 1px; background: var(--pv-line); }
${P} .pv-signin-google { display: flex; justify-content: center; padding: 6px; border-radius: 12px; border: 1px solid var(--pv-line);
  background: var(--pv-glass-liquid-centre); box-shadow: inset 0 1px 0 var(--pv-glass-edge-hi); }
${P} .pv-signin-google[data-highlight="true"] { border-color: var(--pv-accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--pv-accent) 45%, transparent); }
${P} .pv-signin-google[data-working="true"] { opacity: 0.5; }
${P} .pv-signin-suffix { margin: 0; text-align: center; font: 500 13.5px/1.4 var(--pv-font); color: var(--pv-ink-2); text-transform: lowercase; }
${P} .pv-auth-hint { margin-bottom: 16px; }
${P} .pv-signin .pv-k-notice { max-width: none; }
`;

/*
 * THE SIGNED-IN CARD: who, the workspace they are in, the gold "continue", the workspaces to switch to (rows of glass
 * that take the gold edge when current), a pending invitation, and logging out as the glass secondary.
 */
const SIGNED_IN = `
${P} .pv-signin-who { display: flex; align-items: center; gap: 12px; }
${P} .pv-signin-working { margin-top: -6px; }
${P} .pv-signin-workspaces { display: flex; flex-direction: column; gap: 6px; }
${P} .pv-signin-ws-head { margin: 0; font: 600 12px/1.3 var(--pv-font); letter-spacing: .06em; color: var(--pv-ink-3); text-transform: lowercase; }
${P} .pv-signin-ws-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }
${P} .pv-signin-ws { display: flex; align-items: center; gap: 10px; width: 100%; min-height: 44px; padding: 0 12px; border-radius: 12px; cursor: pointer;
  border: 1px solid var(--pv-line); background: var(--pv-glass-liquid-centre); color: var(--pv-ink); font: 600 14.5px/1.3 var(--pv-font); text-align: left; }
${P} .pv-signin-ws[data-active="true"] { border-color: var(--pv-accent); }
${P} .pv-signin-ws:hover:not(:disabled) { border-color: var(--pv-line-control); }
${P} .pv-signin-ws:disabled { opacity: .45; cursor: default; }
${P} .pv-signin-ws:focus-visible { outline: 2px solid var(--pv-focus-ring); outline-offset: 2px; }
${P} .pv-signin-ws-dot { flex: none; color: var(--pv-accent-text); }
${P} .pv-signin-ws-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
${P} .pv-signin-ws-hint { flex: none; font-weight: 500; font-size: 13px; color: var(--pv-ink-2); text-transform: lowercase; }
${P} .pv-signin-error { margin: 0; font: 500 13.5px/1.4 var(--pv-font); color: var(--pv-danger-ink); }
`;

/*
 * THE REFLECTION: a picture of the card's face (./SignInCard.tsx), never a second form. The title, two empty wet-stone
 * boxes with their label lines, the gold bar and a glass bar where Google's button stands.
 */
const PICTURE = `
${P} .pv-signin--picture { gap: 12px; }
${P} .pv-signin-pic-label { display: block; width: 34%; height: 10px; border-radius: 5px; background: var(--pv-line); }
${P} .pv-signin-pic-field { display: block; height: 44px; border-radius: 12px; border: 1px solid var(--pv-line-control); }
${P} .pv-signin-pic-bar { display: block; height: 44px; margin-top: 8px; border-radius: 999px; }
${P} .pv-signin-pic-glass { display: block; height: 52px; margin-top: 16px; border-radius: 12px; border: 1px solid var(--pv-line); background: var(--pv-glass-liquid-centre); }
`;

export const AUTH_CSS = `${STAGE}${JOURNEY}${CARD}${SIGNED_IN}${PICTURE}`;
